import type { WeaponId } from "../content/schemas";
import { shortWeaponName } from "../content/weapons";
import type { LetterGrade, RunSummary } from "./scoring";

const MAX_KILL_SAMPLES = 240;
const MAX_SALVAGE_SAMPLES = 64;

export type DamageTakenSplit = {
  shield: number;
  armor: number;
  hull: number;
};

export type AltitudeTimeBands = {
  nap: number;
  combat: number;
  high: number;
};

export type SalvageSample = {
  t: number;
  total: number;
};

/** Per-run analytics for the debrief screen (presentation + telemetry). */
export type RunDebriefStats = {
  damageTaken: DamageTakenSplit;
  /** Sim-time of each kill (capped). */
  killTimes: number[];
  salvageSamples: SalvageSample[];
  /** @deprecated Retained only to read older persisted run summaries. */
  altitudeTime: AltitudeTimeBands;
  weaponShots: Partial<Record<WeaponId, number>>;
  peakComboMultiplier: number;
};

export type DebriefTone = "crush" | "near" | "triumph" | "secure";

export type DebriefHeroCopy = {
  headline: string;
  kicker: string;
  tone: DebriefTone;
};

export function createRunDebriefStats(): RunDebriefStats {
  return {
    damageTaken: { shield: 0, armor: 0, hull: 0 },
    killTimes: [],
    salvageSamples: [],
    altitudeTime: { nap: 0, combat: 0, high: 0 },
    weaponShots: {},
    peakComboMultiplier: 1,
  };
}

export function cloneRunDebriefStats(stats: RunDebriefStats): RunDebriefStats {
  return {
    damageTaken: { ...stats.damageTaken },
    killTimes: [...stats.killTimes],
    salvageSamples: stats.salvageSamples.map((s) => ({ ...s })),
    altitudeTime: { ...stats.altitudeTime },
    weaponShots: { ...stats.weaponShots },
    peakComboMultiplier: stats.peakComboMultiplier,
  };
}

export function noteDebriefDamage(
  stats: RunDebriefStats,
  shield: number,
  armor: number,
  hull: number,
): void {
  stats.damageTaken.shield += Math.max(0, shield);
  stats.damageTaken.armor += Math.max(0, armor);
  stats.damageTaken.hull += Math.max(0, hull);
}

export function noteDebriefKill(stats: RunDebriefStats, simTime: number, comboMult: number): void {
  if (stats.killTimes.length < MAX_KILL_SAMPLES) {
    stats.killTimes.push(simTime);
  }
  stats.peakComboMultiplier = Math.max(stats.peakComboMultiplier, comboMult);
}

export function noteDebriefSalvage(stats: RunDebriefStats, simTime: number, total: number): void {
  const sample = { t: simTime, total };
  if (stats.salvageSamples.length < MAX_SALVAGE_SAMPLES) {
    stats.salvageSamples.push(sample);
    return;
  }
  // Keep first + evenly spaced later samples by overwriting the last slot.
  stats.salvageSamples[stats.salvageSamples.length - 1] = sample;
}

export function noteDebriefWeaponShot(stats: RunDebriefStats, weaponId: WeaponId): void {
  stats.weaponShots[weaponId] = (stats.weaponShots[weaponId] ?? 0) + 1;
}

/** Emotional headline + tone from outcome + run strength. */
export function debriefHeroCopy(summary: RunSummary): DebriefHeroCopy {
  if (summary.outcome === "extracted") {
    if (summary.grade === "S" || summary.grade === "A") {
      return { headline: "EXTRACT SECURE", kicker: "Mission complete", tone: "triumph" };
    }
    return { headline: "EXTRACT SECURE", kicker: "Pad reached", tone: "secure" };
  }

  if (isStrongDeathRun(summary)) {
    return { headline: "SO CLOSE", kicker: "Hull lost", tone: "near" };
  }
  return { headline: "HULL BREACH", kicker: "Mission failed", tone: "crush" };
}

export function isStrongDeathRun(summary: RunSummary): boolean {
  if (summary.outcome !== "dead") return false;
  return (
    summary.grade === "S" ||
    summary.grade === "A" ||
    summary.grade === "B" ||
    summary.totalKills >= 35 ||
    summary.score >= 7000 ||
    summary.bestChain >= 12
  );
}

export function damageTakenTotal(split: DamageTakenSplit): number {
  return split.shield + split.armor + split.hull;
}

/** Bin kill timestamps into equal-width buckets across [0, duration]. */
export function binKillSparkline(killTimes: number[], durationS: number, buckets = 24): number[] {
  const n = Math.max(4, buckets);
  const out = Array.from({ length: n }, () => 0);
  const span = Math.max(1, durationS);
  for (const t of killTimes) {
    const idx = Math.min(n - 1, Math.max(0, Math.floor((t / span) * n)));
    out[idx]! += 1;
  }
  return out;
}

export function binSalvageSparkline(
  samples: SalvageSample[],
  durationS: number,
  buckets = 24,
): number[] {
  const n = Math.max(4, buckets);
  const out = Array.from({ length: n }, () => 0);
  if (samples.length === 0) return out;
  const span = Math.max(1, durationS);
  let cursor = 0;
  for (let i = 0; i < n; i += 1) {
    const endT = ((i + 1) / n) * span;
    while (cursor + 1 < samples.length && samples[cursor + 1]!.t <= endT) {
      cursor += 1;
    }
    const sample = samples[cursor]!;
    out[i] = sample.t <= endT ? sample.total : i > 0 ? out[i - 1]! : 0;
  }
  return out;
}

export function svgSparkline(values: number[], opts?: { stroke?: string; fill?: string }): string {
  const stroke = opts?.stroke ?? "#c45c26";
  const fill = opts?.fill ?? "rgb(196 92 38 / 22%)";
  const w = 240;
  const h = 48;
  if (values.length === 0 || values.every((v) => v <= 0)) {
    return `<svg class="debrief-spark" viewBox="0 0 ${w} ${h}" aria-hidden="true"><line x1="8" y1="${h / 2}" x2="${w - 8}" y2="${h / 2}" stroke="var(--line)" stroke-width="1"/></svg>`;
  }
  const max = Math.max(...values, 1);
  const step = (w - 16) / Math.max(1, values.length - 1);
  const pts = values.map((v, i) => {
    const x = 8 + i * step;
    const y = h - 6 - (v / max) * (h - 14);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  const line = pts.join(" ");
  const area = `8,${h - 4} ${line} ${(8 + (values.length - 1) * step).toFixed(1)},${h - 4}`;
  return `<svg class="debrief-spark" viewBox="0 0 ${w} ${h}" aria-hidden="true"><polygon points="${area}" fill="${fill}"/><polyline points="${line}" fill="none" stroke="${stroke}" stroke-width="2" stroke-linejoin="round" stroke-linecap="round"/></svg>`;
}

export function svgDamageDonut(split: DamageTakenSplit): string {
  const total = damageTakenTotal(split);
  const size = 88;
  const r = 30;
  const c = 2 * Math.PI * r;
  if (total <= 0) {
    return `<svg class="debrief-donut" viewBox="0 0 ${size} ${size}" aria-hidden="true"><circle cx="44" cy="44" r="${r}" fill="none" stroke="var(--line)" stroke-width="10"/></svg>`;
  }
  const segs: { value: number; color: string }[] = [
    { value: split.shield, color: "#6ec6ff" },
    { value: split.armor, color: "#a89880" },
    { value: split.hull, color: "#d45c4a" },
  ].filter((s) => s.value > 0.05);
  let offset = 0;
  const rings = segs
    .map((seg) => {
      const len = (seg.value / total) * c;
      const dash = `${len.toFixed(2)} ${(c - len).toFixed(2)}`;
      const el = `<circle cx="44" cy="44" r="${r}" fill="none" stroke="${seg.color}" stroke-width="10" stroke-dasharray="${dash}" stroke-dashoffset="${(-offset).toFixed(2)}" transform="rotate(-90 44 44)"/>`;
      offset += len;
      return el;
    })
    .join("");
  return `<svg class="debrief-donut" viewBox="0 0 ${size} ${size}" aria-hidden="true">${rings}<text x="44" y="48" text-anchor="middle" class="debrief-donut-total">${Math.round(total)}</text></svg>`;
}

export function weaponUsageRows(
  shots: Partial<Record<WeaponId, number>>,
): { id: WeaponId; label: string; count: number; pct: number }[] {
  const entries = (Object.entries(shots) as [WeaponId, number][])
    .filter(([, n]) => n > 0)
    .sort((a, b) => b[1] - a[1]);
  const max = Math.max(1, ...entries.map(([, n]) => n));
  return entries.map(([id, count]) => ({
    id,
    label: shortWeaponName(id, id),
    count,
    pct: (count / max) * 100,
  }));
}

export function formatBandSeconds(s: number): string {
  if (s < 10) return `${s.toFixed(1)}s`;
  return `${Math.round(s)}s`;
}

export function gradeIsStrong(grade: LetterGrade): boolean {
  return grade === "S" || grade === "A";
}
